import React, { useRef } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import Errors from "components/base/Errors";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { parseError } from "apollo";
import { PiUploadSimpleBold } from "react-icons/pi";
import useUploadProtection from "hooks/useUploadProtection";

const GET_LANDING_PAGE_FILES = gql`
  query GetLandingPageFiles {
    bizLandingPageFiles(path: "public/biz/landing_page/") {
      totalCount
      images {
        key
        name
        size
        url
        thumbnailUrl
        lastModified
      }
      videos {
        key
        name
        size
        url
        lastModified
      }
    }
  }
`;

const UPLOAD_BIZ_LANDING_MEDIA = gql`
  mutation UploadBizLandingMedia($file: Upload!, $name: String) {
    uploadBizLandingMedia(mediaFile: $file, name: $name) {
      success
      message
      cdnUrl
    }
  }
`;

export default function LandingPage() {
  const { data, loading, error, refetch } = useQuery(GET_LANDING_PAGE_FILES);
  const fileInputRef = useRef(null);
  const [uploadMedia, uploadState] = useMutation(UPLOAD_BIZ_LANDING_MEDIA, {
    onCompleted: (response) => {
      const result = response?.uploadBizLandingMedia;
      Alert(
        result?.success ? "success" : "error",
        result?.message || "Upload failed",
      );
      if (result?.success) refetch();
    },
    onError: (err) => Alert("error", parseError(err)),
  });

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/") && !file.type?.startsWith("video/")) {
      Alert("error", "Only image or video files are supported.");
      event.target.value = "";
      return;
    }

    try {
      await uploadMedia({ variables: { file, name: file.name } });
    } finally {
      event.target.value = "";
    }
  };

  useUploadProtection(
    uploadState.loading,
    "Upload is in progress. Leaving the page may cancel the upload. Continue?",
  );

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const files = data?.bizLandingPageFiles ?? {};
  const videos = files.videos ?? [];
  const images = files.images ?? [];

  return (
    <Page
      title="Landing Page Media"
      rightButtons={
        <div className="flex items-center gap-3">
          {uploadState.loading && (
            <span className="text-sm text-orange-600 animate-pulse whitespace-nowrap">
              Uploading...
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*"
            onChange={handleFileChange}
          />
          <Button
            onClick={handleUploadClick}
            loading={uploadState.loading}
            disabled={uploadState.loading}
            icon={<PiUploadSimpleBold size={18} />}
          >
            Upload Media
          </Button>
        </div>
      }
    >
      <div className="m-6">
        <div className="opacity-70 space-y-2">
          <p>
            Please directly click the button on the top right to upload images
            or videos for the BIZ landing page.
          </p>
        </div>
      </div>

      <div className="px-6 py-8 space-y-10">
        {[
          { title: "Videos", data: videos },
          { title: "Images", data: images },
        ].map(({ title, data }) => (
          <section key={title}>
            <h2 className="text-xl font-bold text-gray-800 tracking-wide mb-6">
              {title}
            </h2>
            {data.length === 0 ? (
              <div className="text-base text-gray-400 text-center py-8">
                No {title.toLowerCase()} available
              </div>
            ) : title === "Videos" ? (
              <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {data.map((item) => (
                  <li
                    key={item.key}
                    className="bg-white rounded-xl shadow hover:shadow-lg transition-all flex flex-col items-center p-5 border border-gray-100"
                  >
                    <button
                      onClick={() => window.open(item.url, "_blank")}
                      className="text-blue-600 hover:text-purple-600 font-medium text-base truncate w-full text-center cursor-pointer"
                    >
                      {item.name || item.key}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="grid gap-8 md:grid-cols-4 sm:grid-cols-2">
                {data.map((item) => (
                  <div
                    key={item.key}
                    className="group bg-white rounded-xl shadow border border-gray-100 overflow-hidden hover:shadow-lg transition-all flex flex-col"
                  >
                    <div
                      onClick={() => window.open(item.url, "_blank")}
                      className="w-full flex justify-center bg-gray-50 group-hover:bg-white transition-colors p-4 cursor-pointer"
                    >
                      <img
                        src={item.thumbnailUrl || item.url}
                        alt={item.name || item.key}
                        className="max-h-48 object-contain transition-transform duration-200 group-hover:scale-105 rounded"
                      />
                    </div>
                    <div className="px-4 py-2 text-sm text-gray-700 break-words text-center border-t border-gray-50 font-medium">
                      {item.name || item.key}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </Page>
  );
}
